// Opening ground when an objective is met.
//
// A sibling of the opponent driver rather than part of it: that one is about
// playing the other side, and this is about the level. Same shape — a
// supervised step that dispatches ordinary commands on a delay.

import { revealStep } from "src/engine/ai/runStep";
import { stageTurnsOver } from "src/engine/ai/waves";

import { Driver } from "./driver";

/** Long enough to read the objective completing before the ground moves. */
const REVEAL_MS = 900;

/** And long enough after that to see what arrived, before the view goes back. */
const LOOK_MS = 1600;

export class RunDirector extends Driver {
  enabled = false;
  /** True while a reveal is on its way, so a wake does not start a second. */
  private busy = false;

  configure(enabled: boolean): void {
    this.enabled = enabled;
    this.wake();
  }

  protected step(): void {
    const { state } = this.door.store.getSnapshot();
    const run = state.run;
    if (!this.enabled || !run || !run.level || this.busy) return;

    // **The decision is `runStep`'s, and so is the order.** A front turning
    // over goes first: both drivers answer to the same event — you beat an
    // army — and they used to do it on independent timers 500ms apart. This
    // one won, and winning broke the other: a `reveal-region` naming a
    // defeated side sweeps that army and recomputes the status, so by the time
    // `next-stage` arrived the reducer refused it, and the camp never spawned.
    // Waiting costs nothing: once the wave has landed this runs on the very
    // next change.
    if (stageTurnsOver(state, run.side)) return;

    const reveal = revealStep(state);
    if (!reveal) return;
    const { command, cells } = reveal;

    this.busy = true;
    // Look first, then open: the camera leads the action, and ground that
    // appears off screen is a board that changed while you were not looking.
    this.look(cells, "region");
    this.later(() => {
      this.door.dispatch(command);
      // Now that it is real, settle on it — then hand the view back.
      this.look(cells, "region");
      this.later(() => {
        this.returnView();
        this.busy = false;
        this.wake();
      }, LOOK_MS);
    }, REVEAL_MS);
  }
}
